import { Badge, Code, Group, Select, Stack, Text, Title } from '@mantine/core';
import { useMemo, useState } from 'react';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import PageLoader from 'src/UI/loader/PageLoader';
import {
  FeatureFlagResponse,
  FeatureFlagStatus,
  useAdminFeatureFlags,
} from './useAdminFeatureFlags';

const STATUS_COLORS: Record<FeatureFlagStatus, string> = {
  fully_released: 'green',
  partially_released: 'orange',
  disabled: 'red',
};

const STATUS_LABELS: Record<FeatureFlagStatus, string> = {
  fully_released: 'Fully released',
  partially_released: 'Partially released',
  disabled: 'Disabled',
};

const COLUMNS: DataTableColumn<FeatureFlagResponse>[] = [
  {
    accessor: 'index',
    title: '#',
    width: 48,
    render: (_: FeatureFlagResponse, index: number) => index + 1,
  },
  {
    accessor: 'name',
    title: 'Flag name',
  },
  {
    accessor: 'key',
    title: 'Flag key',
    render: (row: FeatureFlagResponse) => <Code>{row.key}</Code>,
  },
  {
    accessor: 'maintainer',
    title: 'Maintainer',
    render: (row: FeatureFlagResponse) => row.maintainer ?? '—',
  },
  {
    accessor: 'status',
    title: 'Status',
    width: 180,
    render: (row: FeatureFlagResponse) => (
      <Badge color={STATUS_COLORS[row.status]} variant="light">
        {STATUS_LABELS[row.status]}
      </Badge>
    ),
  },
];

export default function FeatureFlagsPage() {
  const { flags, isLoading, isError } = useAdminFeatureFlags();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [maintainerFilter, setMaintainerFilter] = useState<string | null>(null);

  const sorted = useMemo(
    () => (flags ? [...flags].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [flags],
  );

  const maintainerOptions = useMemo(
    () => [...new Set(sorted.map(f => f.maintainer).filter((m): m is string => m !== null))].sort(),
    [sorted],
  );

  const filtered = useMemo(
    () =>
      sorted.filter(
        f =>
          (!statusFilter || f.status === statusFilter) &&
          (!maintainerFilter || f.maintainer === maintainerFilter),
      ),
    [sorted, statusFilter, maintainerFilter],
  );

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return <Text c="red">Failed to load feature flags</Text>;
  }

  return (
    <Stack gap="md">
      <Title order={3}>LD flag statuses</Title>
      <Group>
        <Select
          placeholder="All statuses"
          clearable
          data={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <Select
          placeholder="All maintainers"
          clearable
          data={maintainerOptions}
          value={maintainerFilter}
          onChange={setMaintainerFilter}
        />
      </Group>
      <DataTable<FeatureFlagResponse>
        withTableBorder={false}
        withColumnBorders={false}
        withRowBorders
        borderColor="gray.3"
        rowBorderColor="gray.3"
        striped
        highlightOnHover
        columns={COLUMNS}
        records={filtered}
        minHeight={200}
        styles={{
          header: {
            backgroundColor: 'var(--mantine-color-gray-0)',
            fontSize: 'var(--mantine-font-size-xs)',
            fontWeight: 600,
            color: 'var(--mantine-color-gray-6)',
          },
        }}
      />
    </Stack>
  );
}
